import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useChat } from '../../context/ChatContext';
import { useDM } from '../../context/DMContext';
import { useChatHub } from '../../context/ChatHubContext';
import ChatTabBar from './ChatTabBar';
import ChatWindow from './ChatWindow';
import DMSelectorModal from './DMSelectorModal';
import ChatHistoryList from './ChatHistoryList';

const ChatManager = () => {
  const { isAuthenticated } = useChat();
  const { conversations, setActiveConversation } = useDM();
  const { updateUnreadCount, updateToggleFunction } = useChatHub();
  const [footerHeight, setFooterHeight] = useState(0);
  
  // Chat mode state - 'tabs' for current interface or 'history' for WhatsApp-like view
  const [chatMode] = useState(() => {
    const saved = localStorage.getItem('chat_mode');
    return saved || 'tabs';
  });
  const [showChatHistory, setShowChatHistory] = useState(false);
  
  // Chat state management
  const [activeChats, setActiveChats] = useState([
    { 
      id: 'global', 
      type: 'global', 
      title: 'Global Chat',
      isMinimized: false,
      unreadCount: 0 
    }
  ]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [allChatsMinimized, setAllChatsMinimized] = useState(() => {
    const saved = localStorage.getItem('chat_all_minimized');
    // Always start minimized for both desktop and mobile
    return saved !== null ? saved === 'true' : true;
  });
  const [chatDimensions, setChatDimensions] = useState(() => {
    const saved = localStorage.getItem('chat_dimensions');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        return { width: 400, height: 500 };
      }
    }
    return { width: 400, height: 500 };
  });
  const [showDMSelector, setShowDMSelector] = useState(false);
  // Initialize closedDMChats with localStorage data immediately
  const [closedDMChats, setClosedDMChats] = useState(() => {
    try {
      const savedClosedChats = localStorage.getItem('chat_closed_dms');
      if (savedClosedChats) {
        const closedIds = JSON.parse(savedClosedChats);
        return new Set(closedIds);
      }
    } catch (error) {
      // Silent error handling for production
      return new Set();
    }
    return new Set();
  });


  // Note: allChatsMinimized and closedDMChats are now initialized directly from localStorage

  // Calculate footer height to prevent overlap
  useEffect(() => {
    const calculateFooterHeight = () => {
      const footer = document.querySelector('footer');
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Check if footer is actually at the bottom of the page
        // Footer should be near the bottom edge of the viewport to be considered
        if (footerRect.top < windowHeight && 
            footerRect.bottom > footerRect.top && 
            footerRect.height > 20 &&
            footerRect.top > windowHeight * 0.7) { // Footer must be in bottom 30% of viewport
          const visibleFooterHeight = Math.max(0, windowHeight - footerRect.top);
          setFooterHeight(visibleFooterHeight);
        } else {
          setFooterHeight(0);
        }
      } else {
        setFooterHeight(0);
      }
    };

    // Wait for page to fully load and layout to stabilize
    const initialDelay = setTimeout(() => {
      calculateFooterHeight();
    }, 1000); // Longer delay for initial load

    // Listen to scroll and resize events for dynamic updates
    window.addEventListener('scroll', calculateFooterHeight);
    window.addEventListener('resize', calculateFooterHeight);

    return () => {
      clearTimeout(initialDelay);
      window.removeEventListener('scroll', calculateFooterHeight);
      window.removeEventListener('resize', calculateFooterHeight);
    };
  }, []);

  // Save states to localStorage
  useEffect(() => {
    localStorage.setItem('chat_all_minimized', allChatsMinimized.toString());
  }, [allChatsMinimized]);

  // Listen for storage events to update minimized state from other components
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'chat_all_minimized' && e.newValue !== null) {
        setAllChatsMinimized(e.newValue === 'true');
      } else if (e.key === 'chat_closed_dms' && e.newValue !== null) {
        try {
          const closedIds = JSON.parse(e.newValue);
          setClosedDMChats(new Set(closedIds));
        } catch (error) {
          console.error('Error parsing closed DM chats from storage:', error);
        }
      } else if (e.key === 'chat_switch_to_conversation' && e.newValue !== null) {
        // Handle conversation switching from storage events
        const conversationId = e.newValue;
        const targetChatIndex = activeChats.findIndex(chat => 
          chat.type === 'dm' && chat.conversationId?.toString() === conversationId
        );
        
        if (targetChatIndex !== -1) {
          setActiveTabIndex(targetChatIndex);
          localStorage.removeItem('chat_switch_to_conversation');
        } else {
          // Retry with exponential backoff - conversation might still be loading
          let retryCount = 0;
          const maxRetries = 5;
          const retryConversationSwitch = () => {
            retryCount++;
            const retryDelay = Math.min(200 * Math.pow(2, retryCount - 1), 2000); // 200ms, 400ms, 800ms, 1600ms, 2000ms
            
            setTimeout(() => {
              setActiveChats(currentChats => {
                const retryIndex = currentChats.findIndex(chat => 
                  chat.type === 'dm' && chat.conversationId?.toString() === conversationId
                );
                
                if (retryIndex !== -1) {
                  setActiveTabIndex(retryIndex);
                  localStorage.removeItem('chat_switch_to_conversation');
                } else if (retryCount < maxRetries) {
                  retryConversationSwitch();
                } else {
                  localStorage.removeItem('chat_switch_to_conversation');
                }
                
                return currentChats; // Return unchanged
              });
            }, retryDelay);
          };
          
          retryConversationSwitch();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [activeChats]);

  useEffect(() => {
    localStorage.setItem('chat_closed_dms', JSON.stringify([...closedDMChats]));
  }, [closedDMChats]);


  // Update active chats when DM conversations change
  useEffect(() => {
    if (!conversations) return;

    setActiveChats(prevChats => {
      const globalChat = prevChats.find(chat => chat.type === 'global');
      const existingDMIds = prevChats.filter(chat => chat.type === 'dm').map(chat => chat.conversationId);
      
      // Add new DM conversations (exclude closed ones, but include closed ones with unread messages)
      const newDMChats = conversations
        .filter(conv => {
          const notExisting = !existingDMIds.includes(conv.conversation_id);
          const notClosed = !closedDMChats.has(conv.conversation_id);
          const hasUnreadMessages = (conv.unread_count || 0) > 0;
          
          // Include if: not existing AND (not closed OR has unread messages)
          const shouldInclude = notExisting && (notClosed || hasUnreadMessages);
          
          // Remove from closed list if we're reopening due to unread messages
          if (shouldInclude && hasUnreadMessages && !notClosed) {
            setClosedDMChats(prev => {
              const newSet = new Set(prev);
              newSet.delete(conv.conversation_id);
              return newSet;
            });
          }
          
          return shouldInclude;
        })
        .map(conv => ({
          id: `dm-${conv.conversation_id}`,
          type: 'dm',
          title: conv.other_display_name || conv.other_username,
          conversationId: conv.conversation_id,
          otherUserId: conv.other_user_id,
          otherUsername: conv.other_username,
          otherDisplayName: conv.other_display_name,
          otherAvatarUrl: conv.other_avatar_url,
          isMinimized: false,
          unreadCount: conv.unread_count || 0,
          lastMessageAt: conv.last_message_at
        }));

      // Update existing DM chats with latest unread counts
      const updatedExistingChats = prevChats.map(chat => {
        if (chat.type === 'dm') {
          const conversation = conversations.find(conv => conv.conversation_id === chat.conversationId);
          if (conversation) {
            return {
              ...chat,
              unreadCount: conversation.unread_count || 0,
              lastMessageAt: conversation.last_message_at,
              title: conversation.other_display_name || conversation.other_username
            };
          }
        }
        return chat;
      });

      // Combine and sort by last message time
      const allChats = [globalChat, ...updatedExistingChats.filter(chat => chat.type === 'dm'), ...newDMChats];
      const sortedChats = allChats.sort((a, b) => {
        if (a.type === 'global') return -1;
        if (b.type === 'global') return 1;
        return new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0);
      });
      
      // Check if we need to switch to a specific conversation
      const switchToConversation = localStorage.getItem('chat_switch_to_conversation');
      if (switchToConversation) {
        const targetChatIndex = sortedChats.findIndex(chat => 
          chat.type === 'dm' && chat.conversationId?.toString() === switchToConversation
        );
        
        if (targetChatIndex !== -1) {
          setTimeout(() => {
            setActiveTabIndex(targetChatIndex);
            localStorage.removeItem('chat_switch_to_conversation');
          }, 100); // Small delay to ensure state updates
        }
        // If not found, the storage event handler will handle retries
      }
      
      return sortedChats;
    });
  }, [conversations, closedDMChats]);

  // Add new DM chat
  const addDMChat = useCallback((conversation, otherUser) => {
    // If conversation is null, this is a new chat that hasn't been created yet
    // We'll use a temporary ID based on the other user's ID
    const chatId = conversation || `temp-${otherUser.id}`;
    
    // Remove from closed list if it was previously closed
    setClosedDMChats(prev => {
      const newSet = new Set(prev);
      newSet.delete(conversation);
      return newSet;
    });

    const newChat = {
      id: `dm-${chatId}`,
      type: 'dm',
      title: otherUser.display_name || otherUser.username,
      conversationId: conversation,
      otherUserId: otherUser.id,
      otherUsername: otherUser.username,
      otherDisplayName: otherUser.display_name,
      otherAvatarUrl: otherUser.avatar_url,
      isMinimized: false,
      unreadCount: 0,
      lastMessageAt: new Date().toISOString(),
      isTemporary: !conversation // Mark as temporary if no conversation ID yet
    };

    setActiveChats(prevChats => {
      // Check if chat already exists (by conversation ID or by other user ID for temp chats)
      const existingIndex = prevChats.findIndex(chat => 
        (conversation && chat.conversationId === conversation) ||
        (!conversation && chat.otherUserId === otherUser.id)
      );
      
      if (existingIndex !== -1) {
        // Activate existing chat
        setActiveTabIndex(existingIndex);
        return prevChats;
      }

      // Add new chat and set as active
      const newChats = [...prevChats, newChat];
      setActiveTabIndex(newChats.length - 1);
      return newChats;
    });
  }, []);

  // Remove DM chat
  const removeDMChat = useCallback((chatId) => {
    setActiveChats(prevChats => {
      // Find the chat being removed to get its conversation ID
      const chatToRemove = prevChats.find(chat => chat.id === chatId);
      if (chatToRemove && chatToRemove.type === 'dm') {
        // Add to closed chats list (can be reopened with unread messages)
        setClosedDMChats(prev => new Set([...prev, chatToRemove.conversationId]));
      }

      const newChats = prevChats.filter(chat => chat.id !== chatId);
      
      // Adjust active tab index if necessary
      if (activeTabIndex >= newChats.length) {
        setActiveTabIndex(Math.max(0, newChats.length - 1));
      }
      
      return newChats;
    });
  }, [activeTabIndex]);

  // Switch to specific chat tab
  const switchToTab = useCallback((index) => {
    setActiveTabIndex(index);
    setAllChatsMinimized(false);
  }, []);

  // Update chat conversation ID when a temporary chat becomes permanent
  const updateChatConversationId = useCallback((chatId, conversationId) => {
    setActiveChats(prevChats => {
      return prevChats.map(chat => {
        if (chat.id === chatId) {
          return {
            ...chat,
            conversationId,
            isTemporary: false,
            id: `dm-${conversationId}` // Update the ID to use the real conversation ID
          };
        }
        return chat;
      });
    });
  }, []);

  // Toggle all chats minimized
  const toggleAllMinimized = useCallback(() => {
    setAllChatsMinimized(prev => !prev);
  }, []);
  
  // Save chat dimensions to localStorage
  useEffect(() => {
    localStorage.setItem('chat_dimensions', JSON.stringify(chatDimensions));
  }, [chatDimensions]);

  // Save chat mode preference
  useEffect(() => {
    localStorage.setItem('chat_mode', chatMode);
  }, [chatMode]);

  // Get total unread count
  const totalUnreadCount = useMemo(() => 
    activeChats.reduce((total, chat) => total + (chat.unreadCount || 0), 0), 
    [activeChats]
  );

  // Update chat hub context
  useEffect(() => {
    updateUnreadCount(totalUnreadCount);
  }, [totalUnreadCount, updateUnreadCount]);

  // Handle DM selection
  const handleDMSelection = useCallback((user) => {
    setShowDMSelector(false);
    // This will be handled by the DMSelectorModal through the DM context
  }, []);

  // Handle conversation selection from history
  const handleHistoryConversationSelect = useCallback((conversation) => {
    // Set the active conversation in DMContext (use conversation_id which is the actual ID)
    setActiveConversation(conversation.conversation_id);
    
    // Find or create the chat tab for this conversation
    const existingChatIndex = activeChats.findIndex(chat => 
      chat.type === 'dm' && chat.conversationId === conversation.conversation_id
    );
    
    if (existingChatIndex !== -1) {
      // Switch to existing tab
      setActiveTabIndex(existingChatIndex);
    } else {
      // Add new chat tab - construct other user object from conversation fields
      const otherUser = {
        id: conversation.other_user_id,
        username: conversation.other_username,
        display_name: conversation.other_display_name,
        avatar_url: conversation.other_avatar_url
      };
      addDMChat(conversation.conversation_id, otherUser);
    }
    
    // Expand chat and close history
    setAllChatsMinimized(false);
    setShowChatHistory(false);
  }, [activeChats, setActiveConversation, addDMChat]);

  // Toggle chat history view
  const toggleChatHistory = useCallback(() => {
    setShowChatHistory(prev => !prev);
  }, []);

  // Update toggle function in chat hub context
  useEffect(() => {
    updateToggleFunction(toggleChatHistory);
  }, [toggleChatHistory, updateToggleFunction]);

  // Handle new conversation from history
  const handleNewConversationFromHistory = useCallback(() => {
    setShowDMSelector(true);
    setShowChatHistory(false);
  }, []);

  // Handle Global Chat selection from history
  const handleGlobalChatSelect = useCallback(() => {
    // Switch to global chat (index 0)
    setActiveTabIndex(0);
    setAllChatsMinimized(false);
    setShowChatHistory(false);
  }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Expose chat functions globally
  useEffect(() => {
    window.updateChatConversationId = updateChatConversationId;
    window.addTemporaryDMChat = addDMChat;
    return () => {
      delete window.updateChatConversationId;
      delete window.addTemporaryDMChat;
    };
  }, [updateChatConversationId, addDMChat]);

  // Update chat hub context
  useEffect(() => {
    const totalUnreadCount = activeChats.reduce((sum, chat) => sum + (chat.unreadCount || 0), 0);
    updateUnreadCount(totalUnreadCount);
  }, [activeChats, updateUnreadCount]);

  useEffect(() => {
    updateToggleFunction(toggleChatHistory);
  }, [toggleChatHistory, updateToggleFunction]);
  

  if (!isAuthenticated) {
    return null;
  }

  // Calculate bottom offset considering footer and minimized state
  const bottomOffset = footerHeight > 0 ? footerHeight + 16 : (allChatsMinimized ? 16 : 0);
  const bottomStyle = { bottom: `${bottomOffset}px` };

  return (
    <>
      {/* Main Chat Interface - only show when expanded */}
      {!allChatsMinimized && (
        <div 
          className="fixed left-0 right-0 md:right-4 md:left-auto md:top-auto z-50"
          style={bottomStyle}
        >
          <div className="flex flex-col w-full md:w-auto md:max-w-none md:shadow-xl md:rounded-lg overflow-hidden" style={!isMobile ? { height: `${chatDimensions.height}px`, width: `${chatDimensions.width}px`, minWidth: '300px', maxWidth: '600px', minHeight: '400px', maxHeight: '800px' } : { height: 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))', maxHeight: 'calc(100dvh - 60px)' }}>
            {/* Tab Bar */}
            <ChatTabBar
              activeChats={activeChats}
              activeTabIndex={activeTabIndex}
              onTabSwitch={switchToTab}
              onMinimizeAll={toggleAllMinimized}
              onCloseDM={removeDMChat}
              onShowHistory={toggleChatHistory}
            />
            
            {/* Active Chat Window */}
            <ChatWindow
              chat={activeChats[activeTabIndex]}
              onMinimize={toggleAllMinimized}
              chatDimensions={chatDimensions}
              setChatDimensions={setChatDimensions}
            />
          </div>
        </div>
      )}

      {/* Chat History Modal */}
      {showChatHistory && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md h-[80vh] max-h-[600px] rounded-lg overflow-hidden shadow-2xl">
            <ChatHistoryList
              onConversationSelect={handleHistoryConversationSelect}
              onNewConversation={handleNewConversationFromHistory}
              onGlobalChatSelect={handleGlobalChatSelect}
              onClose={() => setShowChatHistory(false)}
              activeConversationId={activeChats[activeTabIndex]?.conversationId || (activeChats[activeTabIndex]?.type === 'global' ? 'global' : null)}
            />
          </div>
        </div>
      )}

      {/* DM Selector Modal */}
      {showDMSelector && (
        <DMSelectorModal
          isOpen={showDMSelector}
          onClose={() => setShowDMSelector(false)}
          onSelectUser={handleDMSelection}
          onConversationCreated={addDMChat}
        />
      )}
    </>
  );
};

export default ChatManager;